export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'react-admin-theme';

export function normalizeThemePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

export function resolveIsDark(theme: ThemePreference): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function readStoredResolvedTheme(): 'light' | 'dark' | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'light' || raw === 'dark' ? raw : null;
}

export function applyThemeToDocument(theme: ThemePreference): { isDark: boolean } {
  const isDark = resolveIsDark(theme);
  const root = document.documentElement;
  root.classList.toggle('dark', isDark);
  try {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
  } catch {
    /* localStorage unavailable; DOM state is still correct */
  }
  return { isDark };
}

/**
 * Persist theme preference to the server. Uses `keepalive` so the request
 * survives if the user immediately navigates to another page.
 */
export async function persistThemePreference(theme: ThemePreference): Promise<void> {
  await fetch('/api/auth/preferences', {
    method: 'PUT',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ theme }),
    keepalive: true,
  });
}
